import React from 'react';
import { Box, Divider, Group, Skeleton, Stack, Text, Title } from '@cowbell/ui';

import { AiFactorTrack } from './AiFactorTrack';
import {
  AI_FACTOR_CEILING,
  AI_FACTOR_FLOOR,
  AI_FACTORS,
  type AiFactorScores,
} from './aiFactors.model';
import {
  AI_PANEL_STYLE,
  FOOTER_FONT_SIZE,
  LEGEND_FONT_SIZE,
  POSTURE_INDEX_FONT_SIZE,
  POSTURE_INDEX_FONT_WEIGHT,
  POSTURE_LABEL_FONT_SIZE,
  POSTURE_LABEL_FONT_WEIGHT,
  TILE_SUBTITLE_FONT_SIZE,
  TILE_TITLE_FONT_SIZE,
  TILE_TITLE_FONT_WEIGHT,
  rem,
} from './aiPanelLayout';
import classes from './AiCowbellFactorsPanel.module.css';

export interface AiCowbellFactorsPanelProps {
  readonly data?: AiFactorScores | null;
  readonly isLoading?: boolean;
}

const INTRO =
  'Your AI risk, measured from how your AI actually behaves — updated continuously, with every point traceable to a named cause and a date it clears.';

export const AiCowbellFactorsPanel = ({
  data,
  isLoading = false,
}: AiCowbellFactorsPanelProps) => {
  if (!isLoading && !data) {
    return null;
  }

  const scoreByKey = new Map(
    data?.factors.map((factor) => [factor.key, factor])
  );

  return (
    <Box
      className={classes.panel}
      data-testid="ai-cowbell-factors"
      style={AI_PANEL_STYLE}
    >
      <Group gap={rem(8)} pb={rem(8)} pt={rem(12)}>
        <Title
          fw={TILE_TITLE_FONT_WEIGHT}
          fz={TILE_TITLE_FONT_SIZE}
          m={0}
          order={3}
        >
          AI Cowbell Factors
        </Title>
        <Text c="dimmed" fz={LEGEND_FONT_SIZE}>
          Higher is safer
        </Text>
      </Group>
      <Box className={classes.card}>
        <Stack className={classes.indexColumn} gap={rem(14)}>
          <Text fz={TILE_SUBTITLE_FONT_SIZE}>{INTRO}</Text>
          <Divider />
          <Box className={classes.postureBlock}>
            {isLoading ? (
              <Skeleton
                data-testid="ai-posture-index-loading"
                height={POSTURE_INDEX_FONT_SIZE}
              />
            ) : (
              <Text
                c="violet.6"
                className={classes.postureIndex}
                fw={POSTURE_INDEX_FONT_WEIGHT}
                fz={POSTURE_INDEX_FONT_SIZE}
                ta="center"
              >
                {data?.postureIndex}
              </Text>
            )}
            <Text
              className={classes.postureLabel}
              fw={POSTURE_LABEL_FONT_WEIGHT}
              fz={POSTURE_LABEL_FONT_SIZE}
              ta="center"
              tt="uppercase"
            >
              AI Posture Index
            </Text>
          </Box>
        </Stack>
        <Stack gap={rem(10)} justify="center">
          {isLoading ? (
            <Skeleton
              data-testid="ai-factor-tracks-loading"
              height={rem(144)}
            />
          ) : (
            AI_FACTORS.map((descriptor) => (
              <AiFactorTrack
                delta={scoreByKey.get(descriptor.key)?.delta ?? null}
                factor={descriptor}
                key={descriptor.key}
                score={scoreByKey.get(descriptor.key)?.score ?? 0}
              />
            ))
          )}
          <Text c="dimmed" fz={FOOTER_FONT_SIZE}>
            Grey track spans 0–100 · grey ticks mark the {AI_FACTOR_FLOOR} floor
            and {AI_FACTOR_CEILING} ceiling.
          </Text>
        </Stack>
      </Box>
    </Box>
  );
};
